import type { AgentVoice } from '@tunasong/schemas'

/**
 * Personalize agents — the dialog's words (Joni's UX spec `docs/ux/2026-10-18-personalize-agents-ux.md`,
 * Microcopy). Every voice description paraphrases its `agentVoiceLines` sentence in @tunasong/schemas, so
 * what the band picks matches what the model is told; change them together.
 */
export const personalizeCopy = {
  entry: 'Personalize',
  entryName: (name: string) => `Personalize ${name}`,
  title: (name: string) => `Personalize ${name}`,
  nameLabel: 'Name',
  nameHelper: (defaultName: string) => `Leave blank to use "${defaultName}".`,
  voiceLegend: 'Voice',
  instructionsLabel: 'How your band works',
  instructionsPlaceholder:
    "e.g. We're a punk band — keep it to three chords unless we ask. Don't explain theory terms; we know them.",
  instructionsHelper: (name: string) =>
    `In your words. This shapes how ${name} talks and what it suggests. It can't change what ${name} is for, or edit anything without you.`,
  instructionsOver: (by: number) => `Shorten by ${by} ${by === 1 ? 'character' : 'characters'} to save.`,
  counter: (length: number, max: number) => `${length} / ${max}`,
  charactersLeft: (left: number) => `${left} characters left`,
  overBy: (by: number) => `Over the limit by ${by}`,
  scopeSpace: (space: string) => `Everyone in ${space} sees these changes.`,
  scopePersonal: (name: string, type: string) => `Applies wherever ${name} is used on this ${type}.`,
  reset: 'Reset to default',
  cancel: 'Cancel',
  save: 'Save',
  saved: (name: string) => `Saved. ${name} uses this from its next reply.`,
  undo: 'Undo',
  failed: (name: string) => `Couldn't save ${name}'s settings. Nothing changed.`,
  retry: 'Retry',
  removed: (name: string) => `${name} was removed.`,
  conflict: (name: string) => `A bandmate changed ${name}'s name, voice or instructions while you were editing.`,
  chooseMine: 'Use mine',
  chooseTheirs: 'Load theirs',
  notes: "Following your band's notes.",
} as const

/** The radio's value for "no voice set". Not an `AgentVoice`: choosing it saves `voice: null`. */
export const usualVoice = 'usual'
export type VoiceChoice = AgentVoice | typeof usualVoice

export const voiceOptions: readonly { value: VoiceChoice; label: string; description: string }[] = [
  { value: usualVoice, label: 'Usual', description: 'Its usual way of talking.' },
  { value: 'neutral', label: 'Neutral', description: 'Plain and to the point.' },
  { value: 'friendly', label: 'Friendly', description: 'Warm and informal, like a bandmate.' },
  { value: 'professional', label: 'Professional', description: 'Concise, like a session player.' },
  { value: 'witty', label: 'Witty', description: 'A little dry humour, never instead of an answer.' },
  { value: 'creative', label: 'Creative', description: 'Adds a less obvious idea next to the expected one.' },
]
